import { apiClient } from './api';
import { UserRole } from '../types/api';

// Types
export interface KnowledgeArticle {
  id: string;
  title: string;
  summary?: string;
  content: string;
  htmlContent?: string;
  type: KnowledgeArticleType;
  status: KnowledgeArticleStatus;
  categoryId?: string;
  category?: KnowledgeCategory;
  tags?: string;
  keywords?: string;
  viewCount: number;
  usageCount: number;
  rating: number;
  ratingCount: number;
  isPublic: boolean;
  isFeatured: boolean;
  createdAt: string;
  updatedAt?: string;
  publishedAt?: string;
  authorId: string;
  author?: UserSummary;
  updatedById?: string;
  updatedBy?: UserSummary;
  collaborators: KnowledgeArticleCollaborator[];
  attachments: KnowledgeAttachment[];
  ratings: KnowledgeArticleRating[];
}

export interface KnowledgeArticleSummary {
  id: string;
  title: string;
  summary?: string;
  type: KnowledgeArticleType;
  status: KnowledgeArticleStatus;
  categoryName?: string;
  viewCount: number;
  rating: number;
  ratingCount: number;
  isPublic: boolean;
  isFeatured: boolean;
  createdAt: string;
  publishedAt?: string;
  authorName: string;
}

export interface KnowledgeCategory {
  id: string;
  name: string;
  description?: string;
  parentCategoryId?: string;
  parentCategoryName?: string;
  subCategories: KnowledgeCategory[];
  articleCount: number;
  displayOrder: number;
  isActive: boolean;
  isPublic: boolean;
  icon?: string;
  color?: string;
  createdAt: string;
  updatedAt?: string;
  createdById: string;
  createdByName: string;
}

export interface KnowledgeCategorySummary {
  id: string;
  name: string;
  description?: string;
  parentCategoryId?: string;
  parentCategoryName?: string;
  articleCount: number;
  subCategoryCount: number;
  isActive: boolean;
  isPublic: boolean;
  icon?: string;
  color?: string;
}

export interface KnowledgeArticleCollaborator {
  id: string;
  articleId: string;
  userId: string;
  user?: UserSummary;
  role: CollaboratorRole;
  addedAt: string;
  addedById: string;
  addedByName: string;
}

export interface KnowledgeArticleRating {
  id: string;
  articleId: string;
  userId: string;
  userName: string;
  rating: number;
  comment?: string;
  ratedAt: string;
  updatedAt?: string;
}

export interface KnowledgeAttachment {
  id: string;
  articleId: string;
  fileName: string;
  displayName: string;
  contentType: string;
  fileSize: number;
  storagePath: string;
  uploadedAt: string;
  uploadedById: string;
  uploadedByName: string;
}

export interface KnowledgeSearchRequest {
  query?: string;
  categoryIds?: string[];
  types?: KnowledgeArticleType[];
  statuses?: KnowledgeArticleStatus[];
  publicOnly?: boolean;
  featuredOnly?: boolean;
  createdAfter?: string;
  createdBefore?: string;
  publishedAfter?: string;
  publishedBefore?: string;
  authorIds?: string[];
  tags?: string;
  minRating?: number;
  sortBy?: KnowledgeSearchSortBy;
  sortDescending?: boolean;
  page?: number;
  pageSize?: number;
}

export interface KnowledgeSearchResult {
  articles: KnowledgeArticleSummary[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  query: string;
  searchDuration: string;
}

export interface KnowledgeAnalytics {
  totalArticles: number;
  publishedArticles: number;
  draftArticles: number;
  totalCategories: number;
  totalViews: number;
  totalRatings: number;
  averageRating: number;
  topViewedArticles: KnowledgeArticleSummary[];
  topRatedArticles: KnowledgeArticleSummary[];
  topCategories: KnowledgeCategorySummary[];
  articlesByType: Record<string, number>;
  articlesByStatus: Record<string, number>;
  viewsLastWeek: DailyViews[];
}

export interface DailyViews {
  date: string;
  viewCount: number;
}

export interface UserSummary {
  id: string;
  fullName: string;
  email: string;
  avatarUrl?: string;
  role: UserRole;
}

export interface CreateKnowledgeArticleRequest {
  title: string;
  summary?: string;
  content: string;
  htmlContent?: string;
  type?: KnowledgeArticleType;
  status?: KnowledgeArticleStatus;
  categoryId?: string;
  tags?: string;
  keywords?: string;
  isPublic?: boolean;
  isFeatured?: boolean;
  collaboratorIds?: string[];
}

export interface UpdateKnowledgeArticleRequest {
  title?: string;
  summary?: string;
  content?: string;
  htmlContent?: string;
  type?: KnowledgeArticleType;
  status?: KnowledgeArticleStatus;
  categoryId?: string;
  tags?: string;
  keywords?: string;
  isPublic?: boolean;
  isFeatured?: boolean;
  collaboratorIds?: string[];
}

export interface CreateKnowledgeCategoryRequest {
  name: string;
  description?: string;
  parentCategoryId?: string;
  displayOrder?: number;
  isActive?: boolean;
  isPublic?: boolean;
  icon?: string;
  color?: string;
}

export interface UpdateKnowledgeCategoryRequest {
  name?: string;
  description?: string;
  parentCategoryId?: string;
  displayOrder?: number;
  isActive?: boolean;
  isPublic?: boolean;
  icon?: string;
  color?: string;
}

export interface CreateKnowledgeArticleRatingRequest {
  articleId: string;
  rating: number;
  comment?: string;
}

export interface UpdateKnowledgeArticleRatingRequest {
  rating?: number;
  comment?: string;
}

// Enums
export enum KnowledgeArticleType {
  General = 0,
  HowTo = 1,
  Troubleshooting = 2,
  FAQ = 3,
  Policy = 4,
  Training = 5,
  BestPractice = 6
}

export enum KnowledgeArticleStatus {
  Draft = 0,
  Review = 1,
  Published = 2,
  Archived = 3,
  Deprecated = 4
}

export enum KnowledgeSearchSortBy {
  Relevance = 0,
  Title = 1,
  CreatedDate = 2,
  PublishedDate = 3,
  UpdatedDate = 4,
  ViewCount = 5,
  Rating = 6,
  AuthorName = 7
}

export enum CollaboratorRole {
  Contributor = 0,
  Editor = 1,
  Reviewer = 2,
  Owner = 3
}


// Knowledge Base Service
class KnowledgeBaseService {
  // Article Management
  async getArticles(params: {
    publicOnly?: boolean;
    categoryId?: string;
    status?: KnowledgeArticleStatus;
    type?: KnowledgeArticleType;
    authorId?: string;
    page?: number;
    pageSize?: number;
  } = {}): Promise<KnowledgeArticleSummary[]> {
    console.log('KnowledgeBaseService.getArticles called with params:', params);
    try {
      const response = await apiClient.get('/knowledge/articles', { params });
      console.log('KnowledgeBaseService.getArticles response:', response);
      // Handle both array and paginated responses
      if (Array.isArray(response.data)) {
        return response.data;
      } else if (response.data && response.data.data) {
        // Paginated response
        return response.data.data;
      }
      return [];
    } catch (error) {
      console.error('KnowledgeBaseService.getArticles error:', error);
      throw error;
    }
  }

  async getArticle(id: string, incrementViewCount: boolean = true): Promise<KnowledgeArticle> {
    const response = await apiClient.get(`/knowledge/articles/${id}`, {
      params: { incrementViewCount }
    });
    return response.data;
  }

  async searchArticles(searchRequest: KnowledgeSearchRequest): Promise<KnowledgeSearchResult> {
    const response = await apiClient.post('/knowledge/articles/search', searchRequest);
    return response.data;
  }

  async createArticle(articleData: CreateKnowledgeArticleRequest): Promise<KnowledgeArticle> {
    const response = await apiClient.post('/knowledge/articles', {
      ...articleData,
      categoryId: articleData.categoryId === '' ? null : articleData.categoryId,
    });
    return response.data;
  }

  async updateArticle(id: string, articleData: UpdateKnowledgeArticleRequest): Promise<KnowledgeArticle> {
    const response = await apiClient.put(`/knowledge/articles/${id}`, {
      ...articleData,
      categoryId: articleData.categoryId === '' ? null : articleData.categoryId,
    });
    return response.data;
  }

  async deleteArticle(id: string): Promise<void> {
    await apiClient.delete(`/knowledge/articles/${id}`);
  }

  async publishArticle(id: string): Promise<KnowledgeArticle> {
    const response = await apiClient.post(`/knowledge/articles/${id}/publish`);
    return response.data;
  }

  async archiveArticle(id: string): Promise<KnowledgeArticle> {
    const response = await apiClient.post(`/knowledge/articles/${id}/archive`);
    return response.data;
  }

  async getFeaturedArticles(publicOnly: boolean = false): Promise<KnowledgeArticleSummary[]> {
    const response = await apiClient.get('/knowledge/articles/featured', {
      params: { publicOnly }
    });
    // Handle both array and paginated responses
    if (Array.isArray(response.data)) {
      return response.data;
    } else if (response.data && response.data.data) {
      // Paginated response
      return response.data.data;
    }
    return [];
  }

  async getPopularArticles(count: number = 10, publicOnly: boolean = false): Promise<KnowledgeArticleSummary[]> {
    const response = await apiClient.get('/knowledge/articles/popular', {
      params: { count, publicOnly }
    });
    // Handle both array and paginated responses
    if (Array.isArray(response.data)) {
      return response.data;
    } else if (response.data && response.data.data) {
      // Paginated response
      return response.data.data;
    }
    return [];
  }

  async getTopRatedArticles(count: number = 10, publicOnly: boolean = false): Promise<KnowledgeArticleSummary[]> {
    const response = await apiClient.get('/knowledge/articles/top-rated', {
      params: { count, publicOnly }
    });
    return response.data;
  }

  async getRecentArticles(count: number = 10, publicOnly: boolean = false): Promise<KnowledgeArticleSummary[]> {
    const response = await apiClient.get('/knowledge/articles/recent', {
      params: { count, publicOnly }
    });
    return response.data;
  }

  // Category Management
  async getCategories(publicOnly: boolean = false, includeEmpty: boolean = true): Promise<KnowledgeCategory[]> {
    const response = await apiClient.get('/knowledge/categories', {
      params: { publicOnly, includeEmpty }
    });
    // Handle both array and paginated responses
    if (Array.isArray(response.data)) {
      return response.data;
    } else if (response.data && response.data.data) {
      // Paginated response
      return response.data.data;
    }
    return [];
  }

  async getCategory(id: string): Promise<KnowledgeCategory> {
    const response = await apiClient.get(`/knowledge/categories/${id}`);
    return response.data;
  }

  async createCategory(categoryData: CreateKnowledgeCategoryRequest): Promise<KnowledgeCategory> {
    const response = await apiClient.post('/knowledge/categories', {
      ...categoryData,
      parentCategoryId: categoryData.parentCategoryId === '' ? null : categoryData.parentCategoryId,
    });
    return response.data;
  }

  async updateCategory(id: string, categoryData: UpdateKnowledgeCategoryRequest): Promise<KnowledgeCategory> {
    const response = await apiClient.put(`/knowledge/categories/${id}`, {
      ...categoryData,
      parentCategoryId: categoryData.parentCategoryId === '' ? null : categoryData.parentCategoryId,
    });
    return response.data;
  }

  async deleteCategory(id: string): Promise<void> {
    await apiClient.delete(`/knowledge/categories/${id}`);
  }

  async getArticlesByCategory(
    categoryId: string,
    includeSubcategories: boolean = true,
    publicOnly: boolean = false,
    page: number = 1,
    pageSize: number = 20
  ): Promise<KnowledgeArticleSummary[]> {
    const response = await apiClient.get(`/knowledge/categories/${categoryId}/articles`, {
      params: { includeSubcategories, publicOnly, page, pageSize }
    });
    return response.data;
  }

  // Rating Management
  async rateArticle(articleId: string, ratingData: CreateKnowledgeArticleRatingRequest): Promise<KnowledgeArticleRating> {
    const response = await apiClient.post(`/knowledge/articles/${articleId}/ratings`, ratingData);
    return response.data;
  }

  async updateRating(ratingId: string, ratingData: UpdateKnowledgeArticleRatingRequest): Promise<KnowledgeArticleRating> {
    const response = await apiClient.put(`/knowledge/ratings/${ratingId}`, ratingData);
    return response.data;
  }

  async deleteRating(ratingId: string): Promise<void> {
    await apiClient.delete(`/knowledge/ratings/${ratingId}`);
  }

  async getArticleRatings(articleId: string, page: number = 1, pageSize: number = 20): Promise<KnowledgeArticleRating[]> {
    const response = await apiClient.get(`/knowledge/articles/${articleId}/ratings`, {
      params: { page, pageSize }
    });
    return response.data;
  }

  async getUserArticleRating(articleId: string): Promise<KnowledgeArticleRating | null> {
    try {
      const response = await apiClient.get(`/knowledge/articles/${articleId}/ratings/me`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  // Collaboration Management
  async addCollaborator(articleId: string, userId: string, role: CollaboratorRole = CollaboratorRole.Contributor): Promise<KnowledgeArticleCollaborator> {
    const response = await apiClient.post(`/knowledge/articles/${articleId}/collaborators`, null, {
      params: { userId, role }
    });
    return response.data;
  }

  async removeCollaborator(articleId: string, userId: string): Promise<void> {
    await apiClient.delete(`/knowledge/articles/${articleId}/collaborators/${userId}`);
  }

  async updateCollaboratorRole(collaboratorId: string, newRole: CollaboratorRole): Promise<KnowledgeArticleCollaborator> {
    const response = await apiClient.put(`/knowledge/collaborators/${collaboratorId}/role`, null, {
      params: { newRole }
    });
    return response.data;
  }

  async getArticleCollaborators(articleId: string): Promise<KnowledgeArticleCollaborator[]> {
    const response = await apiClient.get(`/knowledge/articles/${articleId}/collaborators`);
    return response.data;
  }

  // Analytics
  async getAnalytics(startDate?: string, endDate?: string): Promise<KnowledgeAnalytics> {
    const response = await apiClient.get('/knowledge/analytics', {
      params: { startDate, endDate }
    });
    return response.data;
  }

  async incrementArticleUsage(articleId: string): Promise<void> {
    await apiClient.post(`/knowledge/articles/${articleId}/usage`);
  }

  // Attachment Management
  async getArticleAttachments(articleId: string): Promise<KnowledgeAttachment[]> {
    const response = await apiClient.get(`/knowledge/articles/${articleId}/attachments`);
    return response.data;
  }

  async removeAttachment(attachmentId: string): Promise<void> {
    await apiClient.delete(`/knowledge/attachments/${attachmentId}`);
  }
}

export const knowledgeBaseService = new KnowledgeBaseService();
